/**
 * PIR Calculator
 * Calculates optimal number of plates needed for roof covering
 */

class PIRCalculator {
  constructor(element) {
    this.element = element;

    // Read and sanitize configuration from data attributes
    this.targetSelector = this.sanitizeSelector(element.dataset.targetSelector);
    this.productId = this.sanitizeString(element.dataset.productId);
    this.plaatBreedte = this.sanitizeNumber(element.dataset.plateWidth, 1.2);
    this.plaatLengte = this.sanitizeNumber(element.dataset.plateLength, 0.6);
    this.maxDimension = this.sanitizeInteger(element.dataset.maxDimension, 50);

    this.berekendAantalPlaten = 0;
    this.ALERT_DURATION = 5000;

    if (!this.targetSelector) {
      console.warn("PIRCalculator: No valid target selector provided");
      return;
    }

    this.initElements();
    this.attachListeners();
  }

  /**
   * Sanitize and validate CSS selector
   * @param {string} selector - CSS selector string
   * @returns {string|null} - Validated selector or null
   */
  sanitizeSelector(selector) {
    if (!selector || typeof selector !== "string") return null;

    // Only allow safe selector patterns (class, id, attribute selectors)
    const selectorPattern = /^[#.]?[\w\-\[\]="':]+$/;
    if (!selectorPattern.test(selector)) {
      console.warn("PIRCalculator: Invalid selector pattern", selector);
      return null;
    }

    return selector.trim();
  }

  /**
   * Sanitize string input
   * @param {string} value - Input string
   * @returns {string} - Sanitized string
   */
  sanitizeString(value) {
    if (!value || typeof value !== "string") return "";

    // Remove any HTML tags and escape special characters
    return value
      .replace(/[<>'"]/g, "")
      .trim()
      .substring(0, 100); // Limit length
  }

  /**
   * Sanitize and parse number with fallback
   * @param {string|number} value - Input value
   * @param {number} fallback - Fallback value
   * @returns {number} - Validated number
   */
  sanitizeNumber(value, fallback) {
    const parsed = parseFloat(value);
    if (isNaN(parsed) || !isFinite(parsed) || parsed < 0) {
      return fallback;
    }
    return parsed;
  }

  /**
   * Sanitize and parse integer with fallback
   * @param {string|number} value - Input value
   * @param {number} fallback - Fallback value
   * @returns {number} - Validated integer
   */
  sanitizeInteger(value, fallback) {
    const parsed = parseInt(value, 10);
    if (isNaN(parsed) || !isFinite(parsed) || parsed < 0) {
      return fallback;
    }
    return parsed;
  }

  /**
   * Escape HTML for safe text output
   * @param {string} text - Text to escape
   * @returns {string} - Escaped text
   */
  escapeHtml(text) {
    const div = document.createElement("div");
    div.textContent = text;
    return div.innerHTML;
  }

  initElements() {
    // Scope all queries to this calculator instance
    this.formView = this.element.querySelector(".calculator-form-view");
    this.widthInput = this.element.querySelector('[data-input-type="width"]');
    this.lengthInput = this.element.querySelector('[data-input-type="length"]');
    this.wastageCheckbox = this.element.querySelector(
      '[data-input-type="pir-wastage"]'
    );
    this.calculateBtn = this.element.querySelector(
      '[data-action="pir-calculate"]'
    );
    this.applyBtn = this.element.querySelector(
      '[data-action="apply-to-product"]'
    );
    this.restartBtn = this.element.querySelector(
      '[data-action="restart-calculator"]'
    );
    this.alertContainer = this.element.querySelector(
      ".calculator-alert-container"
    );
    this.alertMessage = this.element.querySelector(".calculator-alert-message");
    this.resultsContainer = this.element.querySelector(".calculator-results");
    this.resultDimensions = this.element.querySelector(
      '[data-result="dimensions"]'
    );
    this.resultSurface = this.element.querySelector('[data-result="surface"]');
    this.resultPlates = this.element.querySelector('[data-result="plates"]');

    console.log("PIRCalculator: Elements initialized", {
      formView: !!this.formView,
      widthInput: !!this.widthInput,
      calculateBtn: !!this.calculateBtn,
    });
  }

  attachListeners() {
    if (this.calculateBtn) {
      this.calculateBtn.addEventListener("click", this.calculate.bind(this));
    }
    if (this.applyBtn) {
      this.applyBtn.addEventListener("click", this.applyToProduct.bind(this));
    }
    if (this.restartBtn) {
      this.restartBtn.addEventListener("click", this.restart.bind(this));
    }
  }

  showAlert(message, type = "error") {
    if (!this.alertContainer || !this.alertMessage) return;

    this.alertMessage.textContent = message;
    this.alertMessage.className = `alert calculator-alert-message alert--${type} alert--center`;
    this.alertContainer.style.display = "block";

    setTimeout(() => {
      this.alertContainer.style.display = "none";
    }, this.ALERT_DURATION);
  }

  hideAlert() {
    if (this.alertContainer) {
      this.alertContainer.style.display = "none";
    }
  }

  showResults() {
    // Use inline styles to match HTML structure
    if (this.formView) {
      this.formView.style.display = "none";
    }
    if (this.resultsContainer) {
      this.resultsContainer.style.display = "block";
    }

    // Focus management for accessibility
    const heading = this.resultsContainer?.querySelector("h4");
    if (heading) {
      heading.setAttribute("tabindex", "-1");
      heading.focus();
    }
  }

  restart(e) {
    if (e) e.preventDefault();

    // Reset fields
    if (this.widthInput) this.widthInput.value = "";
    if (this.lengthInput) this.lengthInput.value = "";
    if (this.wastageCheckbox) this.wastageCheckbox.checked = false;

    this.berekendAantalPlaten = 0;

    // Toggle views using inline styles
    if (this.resultsContainer) {
      this.resultsContainer.style.display = "none";
    }
    if (this.formView) {
      this.formView.style.display = "block";
    }

    this.hideAlert();

    // Focus first input
    if (this.widthInput) {
      this.widthInput.focus();
    }
  }

  calculate(e) {
    e.preventDefault();

    const dakBreedte = this.sanitizeNumber(this.widthInput.value, 0);
    const dakLengte = this.sanitizeNumber(this.lengthInput.value, 0);
    const snijverlies = Boolean(this.wastageCheckbox.checked);

    this.hideAlert();

    // Input validation
    if (!dakBreedte || !dakLengte || dakBreedte <= 0 || dakLengte <= 0) {
      this.showAlert("Vul geldige afmetingen in voor beide zijden in.");
      return;
    }

    if (!isFinite(dakBreedte) || !isFinite(dakLengte)) {
      this.showAlert("Ongeldige numerieke waarde ingevoerd.");
      return;
    }

    if (dakBreedte > this.maxDimension || dakLengte > this.maxDimension) {
      this.showAlert(
        "Afmetingen mogen niet groter zijn dan " +
        this.sanitizeInteger(this.maxDimension, 50) + " meter."
      );
      return;
    }

    if (dakBreedte < 0.1 || dakLengte < 0.1) {
      this.showAlert("Afmetingen moeten minimaal 0.1 meter zijn.");
      return;
    }

    // Area-based calculation
    const dakOppervlakte = dakBreedte * dakLengte;
    const plaatOppervlakte = this.plaatBreedte * this.plaatLengte;

    let besteOptie = Math.ceil(dakOppervlakte / plaatOppervlakte);

    // Add wastage if needed
    if (snijverlies) {
      besteOptie = Math.ceil(besteOptie * 1.1);
    }

    this.berekendAantalPlaten = besteOptie;

    const totaalM2 = this.sanitizeNumber((besteOptie * plaatOppervlakte).toFixed(2), 0);

    // Update results
    if (this.resultDimensions) {
      this.resultDimensions.textContent =
        this.sanitizeNumber(dakBreedte, 0).toFixed(1) + " × " +
        this.sanitizeNumber(dakLengte, 0).toFixed(1) + " meter";
    }

    if (this.resultSurface) {
      this.resultSurface.textContent = dakOppervlakte.toFixed(2) + " m²";
    }

    if (this.resultPlates) {
      let platesText = this.sanitizeInteger(besteOptie, 0) +
                       " stuks (totaal " + totaalM2.toFixed(2) + " m²)";
      if (snijverlies) {
        platesText += " incl. snijverlies";
      }
      this.resultPlates.textContent = platesText;
    }

    this.showResults();
  }

  applyToProduct(e) {
    e.preventDefault();

    // Validate calculated value
    const aantalPlaten = this.sanitizeInteger(this.berekendAantalPlaten, 0);

    if (aantalPlaten === 0 || !isFinite(aantalPlaten)) {
      this.showAlert("Bereken eerst het aantal platen.");
      return;
    }

    // Additional safety check for reasonable values
    if (aantalPlaten < 0 || aantalPlaten > 10000) {
      this.showAlert("Berekende waarde is buiten het toegestane bereik.");
      return;
    }

    // Find target field using sanitized selector
    let targetField;
    try {
      targetField = document.querySelector(this.targetSelector);
    } catch (error) {
      console.error("PIRCalculator: Invalid selector", this.targetSelector);
      this.showAlert(
        "Configuratiefout: ongeldige selector. Neem contact op met support.",
        "error"
      );
      return;
    }

    if (!targetField) {
      this.showAlert(
        "Kan het hoeveelheidsveld niet vinden. Neem contact op met support.",
        "error"
      );
      return;
    }

    // Validate target field is an input element
    if (!(targetField instanceof HTMLInputElement) &&
        !(targetField instanceof HTMLTextAreaElement)) {
      this.showAlert(
        "Doelveld is geen geldig invoerveld.",
        "error"
      );
      return;
    }

    // Store old value for potential undo
    const oldValue = targetField.value;

    try {
      // Apply sanitized value as string
      targetField.value = String(aantalPlaten);

      // Trigger events
      ["input", "change"].forEach((eventType) => {
        targetField.dispatchEvent(new Event(eventType, { bubbles: true }));
      });

      // Success feedback with sanitized value
      this.showAlert(
        this.sanitizeInteger(aantalPlaten, 0) + " stuks toegepast op product!",
        "success"
      );

      // Optional: focus the target field
      targetField.focus();
    } catch (error) {
      // Rollback on error
      targetField.value = oldValue;
      console.error("PIRCalculator: Error applying value", error);
      this.showAlert(
        "Er is een fout opgetreden. Probeer het opnieuw.",
        "error"
      );
    }
  }

  destroy() {
    if (this.calculateBtn) {
      this.calculateBtn.removeEventListener("click", this.calculate);
    }
    if (this.applyBtn) {
      this.applyBtn.removeEventListener("click", this.applyToProduct);
    }
    if (this.restartBtn) {
      this.restartBtn.removeEventListener("click", this.restart);
    }
    this.element.removeAttribute("data-calculator-initialized");
  }
}

// Initialize calculators
function initPIRCalculators() {
  console.log("PIRCalculator: Initializing all calculators");

  const calculators = document.querySelectorAll('[data-calculator-type="pir"]');
  console.log("PIRCalculator: Found calculators:", calculators.length);

  calculators.forEach((calculator) => {
    // Prevent double initialization
    if (calculator.hasAttribute("data-calculator-initialized")) {
      return;
    }

    calculator.setAttribute("data-calculator-initialized", "true");
    new PIRCalculator(calculator);
  });
}

// Auto-initialize on page load
if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", initPIRCalculators);
} else {
  initPIRCalculators();
}

// Support Shopify section rendering
document.addEventListener("shopify:section:load", initPIRCalculators);
